import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import {
  Typography,
  buttonBaseClasses,
  menuClasses,
  paperClasses,
  popoverClasses,
  menuItemClasses
} from "@mui/material";
import I18n from "i18next";
import { MzRadio, MzRadioGroup } from "@markaaz/ui/MzRadioControls";
import { MzMenu, MzMenuItem } from "@markaaz/ui/MzMenuControls";

import logoUrl from "assets/logos/markaaz.svg";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import PartnerList from "../user/PartnerList";

const sx = {
  root: {
    maxHeight: "40px",
    backgroundColor: "common.white",
    display: "flex",
    alignItems: "center",
    padding: "20px 30px",
    gap: "48px",
    zIndex: "1201"
  },
  superUser: {
    backgroundColor: "common.secondary"
  },
  menu: {
    [`.${paperClasses.root}.${menuClasses.paper}.${popoverClasses.paper}`]: {
      // height: "204px",
      width: "280px",
      color: "blue.c100",
      padding: "10px 15px 0px 15px"
    },
    [`.${buttonBaseClasses.root}.${menuItemClasses.root}`]: {
      fontWeight: (theme) => {
        return { ...theme.typography.body1M };
      },
      height: "60px",
      borderRadius: "5px"
    }
  },
  menuItems: {
    display: "flex",
    padding: "10px 15px 10px 15px",
    gap: "10px",
    minHeight: "40px",
    borderBottom: (theme) => `1px solid ${theme.palette.blue.c20}`,
    color: "darkBlue.c100"
  },
  menuContent: {
    display: "flex",
    flexDirection: "column"
  },
  spacer: {
    flexGrow: "1"
  },

  avatar: (theme) => ({
    ...theme.typography.h6,
    backgroundColor: theme.palette.lightBlue.c100,
    cursor: "pointer",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    color: theme.palette.common.white
  }),

  profile: { display: "flex", alignItems: "center", gap: "20px" },
  button: { padding: "10px 15px" },
  verticalSpacer: { height: "8px" }
};

export default function Header({
  name,
  isSuperUser,
  isPartnerChanged,
  userPartnerId,
  partners,
  selectedPartner,
  companyLogoUrl,
  onLogout,
  userType,
  companyName,
  environment,
  isPageLoading,
  onSwitch,
  onPartnerChange,
  onRefresh,
  userAccessRole
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [confirmation, setConfirmation] = useState(false);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const disable = userAccessRole === "Reader";

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignout = () => {
    setConfirmation(true);
    handleClose();
  };

  function handleMyAccount() {
    navigate("/my-account");
    handleClose();
  }

  const handleConfirm = () => {
    handleClose();
    onLogout();
  };

  const styles = [sx.root];

  if (isSuperUser) {
    styles.push(sx.superUser);
  }

  return (
    <Box sx={styles}>
      <img width={157} height={40} src={logoUrl} alt="Markaaz Logo" />
      <Box sx={sx.spacer}></Box>
      {isSuperUser && environment === "production" && (
        <PartnerList
          isPartnerChanged={isPartnerChanged}
          isPageLoading={isPageLoading}
          partners={partners}
          selectedPartner={selectedPartner}
          userPartnerId={userPartnerId}
          onPartnerChange={onPartnerChange}
          onRefresh={onRefresh}
        />
      )}
      {userType !== "D" && (
        <Box>
          <MzRadioGroup row name="row-radio-buttons-group" onChange={onSwitch} value={environment}>
            <MzRadio value="sandbox" label="Sandbox" typographyVariant="body1" disabled={isPageLoading || disable} />
            <MzRadio value="production" label="Production" typographyVariant="body1" />
          </MzRadioGroup>
        </Box>
      )}
      <Box sx={sx.profile}>
        {companyLogoUrl ? (
          <img width={157} height={40} src={companyLogoUrl} alt="Company Logo" />
        ) : (
          <Typography variant="quote2">{companyName}</Typography>
        )}

        <Box sx={sx.avatar} onClick={handleClick}>
          {name.slice(0, 1)}
        </Box>
      </Box>
      <MzMenu anchorEl={anchorEl} open={open} onClose={handleClose} sx={sx.menu}>
        <Box sx={sx.menuItems}>
          <Box sx={sx.avatar}>{name.slice(0, 1)}</Box>
          <Box sx={sx.menuContent}>
            <Typography variant="body1M">{name}</Typography>
            <Typography sx={{ color: (theme) => theme.palette.darkBlue.c70 }} variant="caption">
              {userAccessRole}
            </Typography>
          </Box>
        </Box>
        <Box sx={sx.verticalSpacer}></Box>
        <MzMenuItem sx={sx.button} onClick={handleMyAccount}>
          My Account
        </MzMenuItem>
        <MzMenuItem sx={sx.button} onClick={handleSignout}>
          Sign Out
        </MzMenuItem>
      </MzMenu>
      <ConfirmationDialog
        description={`${I18n.t("header.logOutConfirmation")}`}
        onConfirm={handleConfirm}
        open={confirmation}
        handleClose={() => setConfirmation(!confirmation)}
      />
    </Box>
  );
}
